// PORTED COPY — A20 W2b. Original: TPRM7 packages/ui/src/reporter/ReportButton.tsx
// (@vendorwatch/ui). Identical below this header EXCEPT: the `'use client'`
// directive is dropped (Vite SPA, no RSC boundary), and the default geometry is
// 32×32 radius-8 to match AdminHeader's `iconBtn` — the licence console's header
// buttons are 32px, not Topper's 38px. Same reasoning as the original's note:
// the button must read as a sibling of the chrome it sits in.
//
// A20 · W2 — the button that sits beside the help icon.
//
// Geometry deliberately matches the host header's icon buttons so it reads as a
// sibling of the existing chrome rather than a bolted-on widget.

import * as React from 'react';
import { useReporterOptional } from './ReportProvider';

/** Bug glyph — lucide geometry, matching the header's inline icon set. */
function IconBug({ size = 17 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      style={{ display: 'block', flexShrink: 0 }}
    >
      <path d="m8 2 1.88 1.88M14.12 3.88 16 2" />
      <path d="M9 7.13V6a3 3 0 1 1 6 0v1.13" />
      <path d="M12 20a6 6 0 0 0 6-6v-3a4 4 0 0 0-4-4h-4a4 4 0 0 0-4 4v3a6 6 0 0 0 6 6Z" />
      <path d="M6.53 9C4.6 8.8 3 7.1 3 5M6 13H2M3 21c0-2.1 1.7-3.9 3.8-4M20.97 5c0 2.1-1.6 3.8-3.5 4M22 13h-4M17.2 17c2.1.1 3.8 1.9 3.8 4" />
    </svg>
  );
}

export interface ReportButtonProps {
  /** Override the accessible label / tooltip. Defaults to the localised string. */
  label?: string;
  /** Render nothing when no provider is mounted. Default true. */
  hideWhenUnwired?: boolean;
  style?: React.CSSProperties;
}

/**
 * The report affordance. Safe to mount anywhere: with no <ReportProvider> above
 * it, it renders nothing rather than throwing — so a surface can adopt it before
 * its app is wired.
 */
export function ReportButton({ label, hideWhenUnwired = true, style }: ReportButtonProps) {
  const reporter = useReporterOptional();
  const [hover, setHover] = React.useState(false);

  if (!reporter) return hideWhenUnwired ? null : <></>;

  const text = label ?? reporter.t('button.label');

  return (
    <button
      type="button"
      aria-label={text}
      title={text}
      onClick={() => reporter.setOpen(true, 'report')}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      // Never record the reporter's own UI.
      className="ps-no-capture"
      style={{
        position: 'relative',
        width: 32,
        height: 32,
        borderRadius: 8,
        border: 'none',
        cursor: 'pointer',
        background: hover ? 'var(--surface-sunken)' : 'transparent',
        color: 'var(--text-secondary)',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'background 0.12s ease',
        ...style,
      }}
    >
      <IconBug />
    </button>
  );
}
